import React, { useEffect, useState } from "react";
import axios from "axios";
import "../../Styles/ViewAnnouncements.css";
import CancelIcon from "@mui/icons-material/Cancel";
import DeleteIcon from "@mui/icons-material/Delete";
import { toast } from "react-toastify";
import Loader from "../../Components/Loader";

function ViewAnnouncements() {
    const [announcements, setAnnouncements] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
    const [cancelReason, setCancelReason] = useState("");

    useEffect(() => {
        const fetchAnnouncements = async () => {
            setLoading(true);
            try {
                const token = localStorage.getItem("authToken");
                const response = await axios.get(
                    "https://api.uniums.com/api/announcement/view-announcements",
                    {
                        headers: { Authorization: `Bearer ${token}` },
                    }
                );
                setAnnouncements(response.data.data);
                toast.success("Announcements fetched successfully!");
            } catch (error) {
                console.error("Error fetching announcements:", error);
                toast.error("Failed to fetch announcements.");
            } finally {
                setLoading(false);
            }
        };

        fetchAnnouncements();
    }, []);

    const handleCancel = (id) => {
        setShowCancelModal(true);
        setSelectedAnnouncement(id);
    };

    const handleModalSubmit = async () => {
        if (!cancelReason.trim()) {
            toast.error("Please provide a reason for cancellation.");
            return;
        }

        try {
            const token = localStorage.getItem("authToken");
            const response = await axios.put(
                `https://api.uniums.com/api/announcement/update-remark/${selectedAnnouncement}`,
                { remark: cancelReason },
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );

            const updatedAnnouncement = response.data.data;

            // Update the announcements list with the new remark
            setAnnouncements((prev) =>
                prev.map((announcement) =>
                    announcement._id === updatedAnnouncement._id
                        ? updatedAnnouncement
                        : announcement
                )
            );

            setShowCancelModal(false);
            setCancelReason("");
            setSelectedAnnouncement(null);
            toast.success("Remark updated successfully.");
        } catch (error) {
            console.error("Error updating remark:", error);
            toast.error("Failed to update remark.");
        }
    };

    const handleDeleteConfirmation = (id) => {
        setShowDeleteModal(true);
        setSelectedAnnouncement(id);
    };

    const handleDelete = async () => {
        try {
            const token = localStorage.getItem("authToken");
            await axios.delete(`https://api.uniums.com/api/announcement/${selectedAnnouncement}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            toast.success("Announcement deleted successfully!");
            setAnnouncements((prev) => prev.filter((a) => a._id !== selectedAnnouncement));
        } catch (error) {
            console.error("Error deleting announcement:", error);
            toast.error("Failed to delete announcement.");
        } finally {
            setShowDeleteModal(false);
            setSelectedAnnouncement(null);
        }
    };

    return (
        <div className="view-announcements">
            <h1>View Announcements</h1>
            {loading && <Loader />}
            <div className="announcements-grid">
                {announcements.length > 0 ? (
                    announcements.map((announcement) => (
                        <div
                            key={announcement._id}
                            className={`announcements-card ${announcement.isCancelled ? "highlighted" : ""}`}
                        >
                            <h2>{announcement.title}</h2>
                            <p>
                                <strong>Date:</strong> {new Date(announcement.date).toLocaleDateString()}
                            </p>
                            {announcement.time && (
                                <p>
                                    <strong>Time:</strong> {announcement.time}
                                </p>
                            )}
                            <p>
                                <strong>Type:</strong> {announcement.type}
                            </p>
                            <p>
                                <strong>Audience:</strong> {announcement.audience}
                            </p>
                            <p>
                                <strong>Location:</strong> {announcement.location}
                            </p>
                            <p>{announcement.description}</p>
                            {announcement.attachment && (
                                <p>
                                    <strong>Attachment:</strong> <a href={announcement.attachment} target="_blank" rel="noopener noreferrer">View Attachment</a>
                                </p>
                            )}
                            {announcement.remark && (
                                <p>
                                    <strong>Remark:</strong> {announcement.remark}
                                </p>
                            )}

                            <div className="announcements-card-buttons">
                                {!announcement.remark && (
                                    <button
                                        className="announcements-btn cancel"
                                        onClick={() => handleCancel(announcement._id)}
                                    >
                                        <CancelIcon /> Cancel
                                    </button>
                                )}
                                <button
                                    className="announcements-btn delete"
                                    onClick={() => handleDeleteConfirmation(announcement._id)}
                                >
                                    <DeleteIcon /> Delete
                                </button>
                            </div>
                        </div>
                    ))
                ) : (
                    <p>No upcoming announcements found.</p>
                )}
            </div>

            {/* Cancel Modal */}
            {showCancelModal && (
                <div className="modal-ann">
                    <div className="modal-content-announcement">
                        <h2>Cancel Announcement</h2>
                        <textarea
                            placeholder="Provide a reason for cancellation..."
                            value={cancelReason}
                            onChange={(e) => setCancelReason(e.target.value)}
                        />
                        <div className="modal-actions-announcement">
                            <button className="btn-submit-an" onClick={handleModalSubmit}>Submit</button>
                            <button className="btn-close-announcement" onClick={() => { setShowCancelModal(false); setCancelReason(""); setSelectedAnnouncement(null); }}>Close</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Modal */}
            {showDeleteModal && (
                <div className="modal-ann">
                    <div className="modal-content-announcement">
                        <p>Are you sure you want to delete this announcement?</p>
                        <div className="modal-actions-announcement">
                            <button className="btn-submit-an" onClick={handleDelete}>Yes, Delete</button>
                            <button className="btn-close-announcement" onClick={() => { setShowDeleteModal(false); setSelectedAnnouncement(null); }}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ViewAnnouncements;
